import React, { useState } from 'react';
import { formatDate } from '../../services/primitives/date-utils';
import { BaseButton } from '../base';
import { formatTuple, IClaimProgressEvent, JOURNEY_STAGE_LABEL, PHASE_LABEL } from './claim-progress-derivation';
import { ClaimStatePills } from './claim-state-pill';
import * as S from './claim-activity-feed.styles';

interface IClaimActivityFeedProps {
    events: IClaimProgressEvent[];
}

const EMPTY_VALUE = '-';

/** Stage is unknown, not defaulted: an unclassified row says so in the Journey column. */
const journeyLabel = (event: IClaimProgressEvent): string =>
    event.stage === 'UNCLASSIFIED' ? 'Unclassified' : JOURNEY_STAGE_LABEL[event.stage];

export const ClaimActivityFeed: React.FC<IClaimActivityFeedProps> = ({ events }) => {
    const [developerDetails, setDeveloperDetails] = useState(false);

    return (
        <S.Feed>
            <S.FeedHeader>
                <S.FeedTitle>Activity</S.FeedTitle>
                <BaseButton
                    variant={developerDetails ? 'primary' : 'outlined'}
                    size="compact"
                    aria-pressed={developerDetails}
                    onClick={() => setDeveloperDetails(prev => !prev)}
                >
                    Developer details
                </BaseButton>
            </S.FeedHeader>
            <S.Table role="table" aria-label="Claim workflow activity">
                <S.Scroller>
                    <S.HeadRow role="row">
                        <span role="columnheader">Time</span>
                        <span role="columnheader">Journey</span>
                        <span role="columnheader">Phase</span>
                        <span role="columnheader">Event</span>
                        <span role="columnheader" style={{ textAlign: 'right' }}>
                            Outcome
                        </span>
                    </S.HeadRow>
                    {events.length === 0 && <S.EmptyFeed>No workflow events recorded for this claim.</S.EmptyFeed>}
                    {events.map(event => (
                        <S.RowGroup key={event.key}>
                            <S.BodyRow role="row">
                                <S.TimeCell role="cell">
                                    {event.date ? formatDate(event.date, 'MMM d, yyyy h:mma') : EMPTY_VALUE}
                                </S.TimeCell>
                                <S.JourneyCell role="cell">{journeyLabel(event)}</S.JourneyCell>
                                <S.PhaseCell role="cell">
                                    {event.phase === 'UNCLASSIFIED' ? EMPTY_VALUE : PHASE_LABEL[event.phase]}
                                </S.PhaseCell>
                                <S.EventCell role="cell">
                                    <S.EventLabel>
                                        {event.label}
                                        {event.repeatCount > 1 && (
                                            <S.RepeatCount title="Repeated identical waits are grouped">
                                                ×{event.repeatCount}
                                            </S.RepeatCount>
                                        )}
                                    </S.EventLabel>
                                    {event.reworkReason && <S.ReworkNote>Rework: {event.reworkReason}</S.ReworkNote>}
                                </S.EventCell>
                                <S.OutcomeCell role="cell">
                                    <ClaimStatePills event={event} />
                                </S.OutcomeCell>
                            </S.BodyRow>
                            {developerDetails && (
                                <S.DeveloperRow>
                                    <S.Tuple>
                                        {formatTuple(event.status, event.step, event.action, event.attempt)}
                                    </S.Tuple>
                                    {event.detail && <S.DetailText>{event.detail}</S.DetailText>}
                                </S.DeveloperRow>
                            )}
                        </S.RowGroup>
                    ))}
                </S.Scroller>
            </S.Table>
            <S.Footnote>
                <span>Repeated identical waits are grouped.</span>
                <span>Raw Status / Step / Action in developer details.</span>
            </S.Footnote>
        </S.Feed>
    );
};
